import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
  makeStyles,
} from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import { Link } from "react-router-dom";

import useTask from "../hooks/useTask";
import { IReduxAppState } from "../reducers";
import * as URLS from "../const/URL.const";
import { ITask, ISubTask } from "../yoello-lib/modules/interfaces/ITask";
import { outstandingSwitch } from "../actions/outstanding";
import { PaymentProcessor } from "./menu/payments/types.d";

interface urls {
  [key: string]: {
    [key: string]: string;
  };
}


export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    outstanding: {
      fontSize: "1rem",
      flexShrink: 0,
      padding: "0.2rem 0",
      "& .MuiDialog-paper": {
        borderRadius: "1.1rem",
      },

      "& .header": {
        textAlign: "left",
        display: "flex",
        justifyContent: "space-between",
        color: "#ffffff",
        backgroundColor: "#212121",
        padding: "0.5rem 1rem",
        lineHeight: "2rem",
        borderRadius: "0.625rem",
        cursor: "pointer",
        fontWeight: "600",
      },
      "& .title": {
        fontStyle: "normal",
        fontWeight: "normal",
        fontSize: "1rem",
        lineHeight: "1.75rem",
        color: "#737FA4",
        height: "3.5rem",
        top: "16.875rem",
      },
      "& .group": {
        margin: 0,
        marginTop: "0.5rem",
        display: "flex",

        "& .label": {
          lineHeight: "2rem",
          flexGrow: "10",
          fontSize: "1.0625rem",
          color: "#212121",
        },

        "& .complete": {
          marginRight: "1.25rem",
        },

        "&.complete": {
          "& .label": {
            opacity: "0.4",
            textDecoration: "line-through",
          },
        },

        "& .completed": {
          color: "#4F3CC9",
        },

        "& .status": {
          color: "#212121",
          fontWeight: "400",
        },
      },

      "& .item": {
        display: "flex",
        padding: "0.125rem 0.625rem",
        borderRadius: "0.625rem",

        "&:hover": {
          backgroundColor: "#F3F4F9",
          paddingLeft: "0.5rem",
        },

        "& .label": {
          display: "flex",
          lineHeight: "2rem",
          flexGrow: "10",
          marginLeft: "-0.5rem",
          alignItems: "center",
          color: "#8D98B7",

          "& a": {
            textDecoration: "none",
            display: "flex !important",
            height: "100%",
            alignItems: "center",
            lineHeight: "1.6rem",
            color: "#8D98B7",

            "&:hover": {
              color: "#4F3CC9",
            },
          },

          "&::before": {
            content: "''",
            display: "block",
            float: "left",
            width: "1rem",
            height: "1.6rem",
            backgroundPosition: "left",
            backgroundRepeat: "no-repeat",
            backgroundSize: "50%",
            backgroundImage:
              "url(data:image/png,base64,iVBORw0KGgoAAAANSUhEUgAAAAYAAAAKCAMAAACg0N8BAAAAMFBMVEUAAACPn6+Hl7ePl7eKlbWKmrWPlbqOmbeNl7eOmLiNmbaPmbeLl7eNmLeNl7iNmbj2nBl0AAAAEHRSTlMAECAgMDAwX2BvcH+A3+/vgmDQqgAAAC9JREFUeNotxVEaABAIBOFVAMr9b8v2mYf5AQErJ4fdJJzfbYks0/cRb86LVzDlLhvhAMroMzn9AAAAAElFTkSuQmCC)",
          },
        },

        "&.complete": {
          "& .label": {
            opacity: "0.4",
            textDecoration: "line-through",
            marginLeft: "-1.375rem",
          },

          "& .label::before": {
            backgroundImage: "none !important",
          },
        },

        "& .status": {
          width: "2rem",
          height: "2rem",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "60%",
          backgroundImage:
            "url(data:image/png,base64,iVBORw0KGgoAAAANSUhEUgAAABMAAAATCAMAAABFjsb+AAAAUVBMVEUAAABQOMdPPMlOO8hPPMpPO8hPO8lPPclPPMlPPMlaScxlVNBlVc9wYNNwYdORht6ckuGnnuS9teu9tuvIwu7Iwu/e2/Xp5/j08/v08/z////6LcfkAAAACXRSTlMAIK+wv9/f3+99Wys7AAAAa0lEQVR42oXQAxbEQBAE0OrYTgb3v+d6M7b+QwOgomrVaDJ6UaPgq4SstUeBxrEarTuS1l37bTfxUdmP5NzeNgzv83iRslNMbbu9SVnP2LDJpVX2QS5XO5eJrYmcS4caFI7l3v6BMl2rgvAE3MINZ4sr73AAAAAASUVORK5CYII=)",
        },
      },

      "&.open": {
        "& .toggle": {
          transform: "rotate(0deg)",
          borderRadius: "1rem",
        },

        "& .contents": {
          display: "block",
        },
      },
    },
  })
);

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
      paddingLeft: "40px",
      fontSize: "16px",
      backgroundColor: "#212121",
      height: "60px",
      color: "#FFFFFF",
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: "#FFFFFF",
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;

  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    paddingInline: theme.spacing(9),
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "16px",
  },
}))(MuiDialogContent);

const Outstanding = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const { doneTasks, totalTasks } = useTask();
  const venueID = useSelector((state: IReduxAppState) => state.selectedVenueId);
  const Venue = useSelector(
    (state: IReduxAppState) => state.venue[venueID]?.venueData
  );
  const isEposNow = useSelector((state: IReduxAppState) => state.isEposNow);
  const open = useSelector((state: IReduxAppState) => state.outstanding.open);
  const outstandingData = useSelector(
    (state: IReduxAppState) => state.outstanding.body
  );

  const isMigrationOpen = useSelector(
    (state: IReduxAppState) => state.migrations.isOpen
  );

  const sidebar = useSelector(
    (state: IReduxAppState) => state.sidebar.position.key
  );
  


const urlObj: urls = {
  details: {
    setUp: URLS.PROFILE_DETAILS_URL,
  },
  payments: {
    setUp: URLS.PROFILE_PAYMENTS_URL,
  },
  subscription: {
    setUp: URLS.SUBSCRIPTION_URL,
  },
  products: {
    subcategories: URLS.PRODUCTS_SUBCATEGORIES,
    items: URLS.PRODUCTS_ITEMS_URL,
    sync: isEposNow ? URLS.PROFILE_EPOS_NOW : URLS.PROFILE_MENU_SYNC,
    syncing: URLS.PROFILE_EPOS_NOW,
    menu: URLS.PRODUCTS_MANAGE_MENU_URL
  },
  settings: {
    order: URLS.PROFILE_ORDER_SETTINGS_URL,
    tradingtimes: URLS.PROFILE_TRADING_TIMES_URL,
    images: URLS.PROFILE_VENUE_IMAGES,
  },
  qrcode: {
    url: URLS.PROFILE_VENUE_URL,
  },
  taxes: {
    setUp: URLS.TAXES_URL,
  },
  oauth: {
    accessToken: URLS.PROFILE_DETAILS_URL,
  },
};
  useEffect(() => {
    if (isMigrationOpen) {
      handleClose();
    }
    // eslint-disable-next-line
  }, [open, isMigrationOpen]);

  useEffect(() => {
    if (Venue?.payment_processor === PaymentProcessor.SQUARE) {
      urlObj.products.sync = URLS.SQUARE_SYNC;
    }
    // eslint-disable-next-line
  }, [Venue]);

  const handleClose = () => {
    dispatch(outstandingSwitch(false));
  };

  const renderItems = () => {
    return outstandingData.map((group: ITask) => {
      const groupTotal = group.items.length;
      const groupDone = group.items.filter((item: ISubTask) => item.complete)
        .length;
      const groupComplete =
        groupTotal === groupDone
          ? t("outstanding.completed")
          : `${groupDone}/${groupTotal}`;
      const classNameGroup = clsx("group", {
        complete: groupTotal === groupDone,
      });

      const initialValue = (
        <h4 className={classNameGroup}>
          <div className="label">{t(`outstanding.${group.task}`)}</div>

          <div
            className={
              groupTotal === groupDone ? "complete completed" : "complete"
            }
          >
            {groupComplete}
          </div>
        </h4>
      );

      return group.items.reduce(
        (total: JSX.Element[], item: ISubTask) => {
          const itemComplete = item.complete && (
            <div className="complete"></div>
          );
          const classNameItem = clsx("item", {
            complete: item.complete,
          });
          const newItem = (
            <div className={classNameItem}>
              <div className="label">
                {item.complete ? (
                  <div>{t(`outstanding.${group.task}${item.subtask}`)}</div>
                ) : (
                  <Link
                    to={urlObj[group.task][item.subtask]}
                    onClick={handleClose}
                  >
                    {t(`outstanding.${group.task}${item.subtask}`)}
                  </Link>
                )}
              </div>
              <Checkbox checked={item.complete} disableRipple={true} />
              {itemComplete}
            </div>
          );

          return [...total, newItem];
        },
        [initialValue]
      );
    });
  };

  const className = clsx(classes.outstanding, {
    open: open,
  });

  return doneTasks < totalTasks && sidebar !== "OVERVIEW" ? (
    <div className={className}>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        className={classes.outstanding}
        style={{ zIndex: 1200 }}
      >
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          {t("outstanding.title")} {`${doneTasks}/${totalTasks}`}
        </DialogTitle>

        <DialogContent dividers style={{ borderBottom: "none" }}>
          <h6 className="title">{t("outstanding.modalTitle")}</h6>
          {renderItems()}
        </DialogContent>
      </Dialog>
    </div>
  ) : null;
};

export default Outstanding;
